import { Capacitor } from '@capacitor/core';
import { App as CapacitorApp } from '@capacitor/app';
import { Preferences } from '@capacitor/preferences';
import localforage from 'localforage';
import { ensurePersistentStorage } from './persistentStorage';
import { markBoot, withDeadline } from '../utils/bootTrace';
import { getSessionStorageUser } from '../utils/sessionStorage';

/**
 * Almacenamiento duradero de la app nativa.
 *
 * ─── Por qué no basta con `localStorage` ───────────────────────────────────
 *
 * La primera versión de esto espejaba la sesión a `localStorage`. Funcionaba,
 * pero la documentación de Capacitor es explícita sobre por qué no alcanza:
 *
 *   «Local Storage can be used for small amounts of temporary data […] but
 *    must be considered transient […] the OS will reclaim local storage from
 *    Web Views if a device is running low on space.»
 *
 * Es decir: sobrevive a cerrar la app, pero **Android puede llevárselo** si el
 * dispositivo va justo de disco. Para una tablet de campo eso significa un
 * operario deslogueado a media jornada sin ninguna explicación — exactamente
 * el fallo que la persistencia venía a evitar.
 *
 * ─── Tres niveles según lo que se juega cada dato ──────────────────────────
 *
 *   Sesión (3 claves, pequeñas y críticas)
 *     → **Preferences**: `SharedPreferences` de Android. Almacenamiento
 *       nativo, fuera del WebView, inmune al desalojo. Como beneficio extra,
 *       las limpiezas de `localStorage` que hace la propia app (el botón
 *       "Limpiar caché", el modal de versión) ya no pueden tocarlo.
 *
 *   Borradores de wizard y croquis (muchos y grandes)
 *     → **IndexedDB**, que en Android sí admite marcarse como persistente
 *       vía `navigator.storage.persist()`, y cuya cuota se mide en cientos de
 *       MB en vez de en los ~5 MB de `localStorage`.
 *
 *   Todo lo demás
 *     → `sessionStorage`, efímero como hoy. Snapshots de la cola offline,
 *       handoffs entre pantallas y migas de navegación NO deben persistir:
 *       restaurados crean fallos nuevos (un modal disparándose con datos de
 *       ayer) en vez de resolver ninguno.
 *
 * ─── Cómo se resuelve que Preferences sea asíncrono ────────────────────────
 *
 * La sesión se lee de forma SÍNCRONA en decenas de sitios, incluidos loaders
 * del router que corren antes del primer pintado. Preferences devuelve
 * promesas, así que no puede sustituir esas lecturas.
 *
 * La solución es leer **una sola vez, en el arranque, antes de montar React**:
 * se vuelca de Preferences a `sessionStorage` y a partir de ahí todo el código
 * existente funciona sin tocar una línea. Las escrituras siguen yendo a
 * `sessionStorage` (síncronas, inmediatas) y se replican al nivel duradero en
 * segundo plano.
 *
 * En web todo esto es un no-op: `sessionStorage` conserva su semántica por
 * pestaña, que es la correcta cuando alguien abre dos concesiones a la vez.
 */

/** Sesión: poco dato, crítico. Va a Preferences. */
const SESSION_KEYS = ['user', 'activeTenantId', 'appConfig'] as const;

const PREF_PREFIX = 'rois.session.';

/** Un borrador más viejo que esto apunta casi seguro a una actuación ya
 * validada o borrada por otro: retomarlo confunde más de lo que ayuda. */
const DRAFT_MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000;

/**
 * Plazos del arranque. Ninguno es un ajuste de rendimiento: son el tope a
 * partir del cual damos por hecho que la dependencia NO va a contestar nunca.
 *
 * Las tres operaciones son locales (SharedPreferences, IndexedDB) y en una
 * tablet sana tardan milisegundos. Tres segundos es dos órdenes de magnitud de
 * margen; pasado eso, lo que hay no es lentitud, es un cuelgue.
 *
 * Pasarse de generoso tiene un coste concreto: es tiempo de pantalla blanca
 * delante del operario.
 */
const PERSISTENCE_DEADLINE_MS = 2_000;
const SESSION_DEADLINE_MS = 3_000;
const DRAFTS_DEADLINE_MS = 3_000;

/**
 * Lo máximo que puede tardar `bootstrapNativeStorage` en el peor caso.
 *
 * Se exporta para que la red de seguridad de `main.tsx` se calcule A PARTIR de
 * aquí en vez de llevar un número copiado: si alguien sube un plazo, el
 * watchdog sube con él. Con el número a mano, bastaba tocar un plazo para que
 * el rescate se adelantara a un arranque que iba a terminar solo.
 */
export const NATIVE_BOOTSTRAP_BUDGET_MS =
  PERSISTENCE_DEADLINE_MS + SESSION_DEADLINE_MS + DRAFTS_DEADLINE_MS;

const draftStore = localforage.createInstance({
  name: 'rois',
  storeName: 'native_drafts',
});

type StoredDraft = { ts: number; value: string; ns: string };

function isNative(): boolean {
  return Capacitor.isNativePlatform();
}

function isSessionKey(key: string): boolean {
  return (SESSION_KEYS as readonly string[]).includes(key);
}

/**
 * Claves que deben sobrevivir a que Android mate la app: borradores de los
 * wizards y croquis.
 *
 * Se detectan por FORMA (`new-*`, `edit-*`, `add-*`, `*canvas*`) y no por una
 * lista cerrada, porque una lista se quedaría atrás en cuanto alguien añada un
 * wizard — y el fallo sería silencioso: el operario perdería ese formulario y
 * nadie sabría por qué.
 */
function isDurableKey(key: string): boolean {
  return /^(new|edit|add)-/.test(key) || key.includes('canvas');
}

function hash36(text: string): string {
  let h = 0;
  for (let i = 0; i < text.length; i++) h = (h * 31 + text.charCodeAt(i)) | 0;
  return (h >>> 0).toString(36);
}

/**
 * Huella ANTIGUA del dueño: el contenido entero de la sesión. Solo se usa para
 * reconocer borradores guardados antes del cambio (transición y logout).
 */
function legacyNamespace(): string | null {
  const raw = sessionStorage.getItem('user');
  return raw ? `u${hash36(raw)}` : null;
}

let lastRaw: string | null = null;
let lastNs: string | null = null;

/**
 * Dueño de los borradores: el USUARIO y su concesión activa, para que en una
 * tablet compartida el borrador de un operario no reaparezca en la sesión del
 * siguiente, ni en otra concesión. Se guarda como huella: no queda ningún dato
 * del usuario en el almacén.
 *
 * 🔴 Antes era una huella del contenido ENTERO de la sesión, token incluido. El
 * token se renueva cada hora: si se renovaba en segundo plano (GPS, colas)
 * después del último volcado y Android mataba la app, al reabrir ningún
 * borrador coincidía y se perdían todos (2026-10-12). Usuario + concesión es
 * lo mismo que sella las colas desde #331 (`offline/queueOwner.ts`).
 *
 * Se recuerda el último cálculo: el espejo lo pide en cada escritura de un
 * borrador (cada tecla) y la sesión va comprimida en el build.
 */
function namespace(): string | null {
  const raw = sessionStorage.getItem('user');
  if (!raw) return null;
  if (raw === lastRaw) return lastNs;
  const user = getSessionStorageUser()?.user;
  const ns =
    user?.id && user?.activeTenantId
      ? `o${hash36(`${user.id}|${user.activeTenantId}`)}`
      : null;
  lastRaw = raw;
  lastNs = ns;
  return ns;
}


/** Vuelca la sesión guardada a `sessionStorage`. La sesión viva siempre manda. */
async function restoreSession(isAbandoned: () => boolean): Promise<void> {
  // En PARALELO y no en serie: son tres viajes por el puente de Capacitor y en
  // serie se suman: el plazo de arriba lo comparten los tres.
  await Promise.all(
    SESSION_KEYS.map(async (key) => {
      if (sessionStorage.getItem(key) !== null) return;
      try {
        const { value } = await Preferences.get({ key: PREF_PREFIX + key });
        // 🔴 Si el plazo ya venció, esta lectura llega TARDE y se descarta
        // entera. El plazo no la cancela, así que puede volver con la app ya
        // viva, y para entonces no aporta nada —`AuthProvider` ya leyó la
        // sesión al montarse— y sí puede hacer daño en una tablet COMPARTIDA:
        // meter la sesión del turno anterior encima del operario que acaba de
        // entrar, resucitar una sesión recién cerrada (la clave vacía no
        // distingue «nunca puesta» de «borrada por el logout»), o mezclar el
        // `appConfig` de uno con el `user` de otro.
        if (isAbandoned()) return;
        // Y la sesión viva sigue mandando aunque se llegue a tiempo.
        if (value !== null && sessionStorage.getItem(key) === null) {
          sessionStorage.setItem(key, value);
        }
      } catch {
        // Preferences no disponible: se arranca sin sesión, que es el
        // comportamiento de siempre, no una regresión.
      }
    })
  );
}

/** Devuelve los borradores del usuario que ha vuelto y tira los caducados. */
async function restoreDrafts(isAbandoned: () => boolean): Promise<void> {
  const ns = namespace();
  // Transición: los borradores guardados antes del cambio llevan la huella
  // antigua (sesión entera). Si coincide con la de ahora se devuelven y se
  // re-sellan con la nueva; si no, son de antes de una renovación y se
  // perdieron con el esquema viejo — nada que hacer.
  const legacyNs = legacyNamespace();
  const now = Date.now();
  try {
    const keys = await draftStore.keys();
    for (const key of keys) {
      // Abandonado = la app ya arrancó sin estos borradores. Seguir escribiendo
      // mete borradores con el namespace de QUIEN ESTABA al empezar: si entre
      // medias ha cambiado el operario, el espejo los re-guarda con el
      // namespace del nuevo y le aparecen los formularios del anterior. Y si
      // no ha cambiado, resucita borradores que ya envió.
      if (isAbandoned()) return;
      const entry = await draftStore.getItem<StoredDraft>(key);
      if (isAbandoned()) return;
      if (!entry) continue;
      if (now - entry.ts > DRAFT_MAX_AGE_MS) {
        await draftStore.removeItem(key);
        continue;
      }
      // De otro operario: ni se restaura ni se borra (lo borra su logout).
      const isLegacy = !!legacyNs && entry.ns === legacyNs;
      if (!ns || (entry.ns !== ns && !isLegacy)) continue;
      if (sessionStorage.getItem(key) === null) {
        sessionStorage.setItem(key, entry.value);
      }
      // El espejo se instala DESPUÉS del arranque (main.tsx), así que lo
      // restaurado no se re-sella solo: se hace aquí.
      if (isLegacy) await draftStore.setItem<StoredDraft>(key, { ...entry, ns });
    }
  } catch {
    // Un borrador ilegible no puede impedir que la app arranque.
  }
}

/**
 * Arranque del almacenamiento nativo. **Debe esperarse antes de montar React**:
 * los loaders del router leen la sesión en el primer render, así que llegar
 * tarde equivale a no haberlo hecho.
 */
export async function bootstrapNativeStorage(): Promise<void> {
  if (!isNative()) return;
  markBoot('native-storage:start');

  // 🔴 Cada paso con PLAZO. Los tres eran `await` pelados y cualquiera de ellos
  // podía no volver nunca —el puente de Capacitor o IndexedDB no rechazan, se
  // quedan pendientes—, y como esto se espera antes de montar React, el
  // resultado era pantalla blanca permanente: el estado que lo provoca está en
  // disco, así que se repetía en cada arranque hasta reinstalar la app.
  //
  // Un `try/catch` no servía: atrapa el rechazo, no el silencio. Solo el reloj
  // distingue «tarda» de «no va a contestar».
  //
  // El orden se conserva y los plazos NO se comparten: lo que sí llegue a
  // tiempo se aplica aunque el paso siguiente venza. Perder los borradores es
  // una molestia; perder la sesión sería sacar al operario de la app.

  // Persistencia del almacén: la pide el módulo canónico `persistentStorage`,
  // que main introdujo con reintento al encolar trabajo. Tener DOS sitios
  // llamando a `navigator.storage.persist()` no daba más garantías y sí dos
  // logs contradictorios cuando uno se concedía y el otro salía de la caché.
  // (Cada paso apunta su hito al terminar a tiempo; si vence, va a la lista
  // de vencidos. Ver `withDeadline`.)
  await withDeadline(
    () => ensurePersistentStorage('native app boot'),
    PERSISTENCE_DEADLINE_MS,
    false,
    'native-storage:persistence'
  );

  await withDeadline(
    restoreSession,
    SESSION_DEADLINE_MS,
    undefined,
    'native-storage:session'
  );

  // Los borradores DESPUÉS de la sesión: su namespace depende de quién sea el
  // usuario que acaba de volver.
  await withDeadline(
    restoreDrafts,
    DRAFTS_DEADLINE_MS,
    undefined,
    'native-storage:drafts'
  );
}

/**
 * Envuelve `sessionStorage` para replicar cada escritura al nivel duradero que
 * le corresponde.
 *
 * Se envuelve el API en vez de tocar los ~165 ficheros que la usan, y no es por
 * pereza: cambiarlos uno a uno dejaría la puerta abierta a que un sitio nuevo
 * se olvide de replicar, y ese fallo sería intermitente y no se reproduciría en
 * la oficina. Envolviendo, es imposible saltárselo.
 */
/**
 * Escrituras del espejo que todavía no han terminado.
 *
 * El espejo es fire-and-forget: `setItem` devuelve el control al formulario sin
 * esperar a que IndexedDB confirme, que es lo correcto —bloquear cada tecla
 * contra el disco haría el wizard inusable—. El precio es que si Android mata
 * el proceso justo después de teclear, esa escritura puede no haber cuajado.
 * Aquí se registran para poder esperarlas cuando la app pasa a segundo plano,
 * que es el único aviso que da Android antes de matar.
 */
const pendingWrites = new Set<Promise<unknown>>();

function track(p: Promise<unknown>): void {
  pendingWrites.add(p);
  void p.finally(() => pendingWrites.delete(p));
}

/**
 * Vuelca a disco todo lo que deba sobrevivir, y espera a que termine.
 *
 * Se llama cuando la app pasa a segundo plano. Hace dos cosas:
 *
 * 1. **Espera las escrituras en vuelo.** Mientras la app está en primer plano
 *    hay CPU garantizada; una vez en segundo plano, Android puede congelar el
 *    proceso en cualquier momento.
 * 2. **Reconcilia el estado completo.** Recorre `sessionStorage` y reescribe lo
 *    que toca. Es idempotente y son cuatro claves, así que sale barato — y
 *    recupera cualquier escritura que fallara en silencio o que ocurriera antes
 *    de instalar el espejo.
 *
 * No es una garantía dura: si Android decide matar el proceso a mitad, se
 * pierde igual. Es la diferencia entre perder la última pulsación y perder el
 * formulario entero.
 */
export async function flushDurableStorage(): Promise<void> {
  if (!isNative()) return;

  await Promise.allSettled([...pendingWrites]);

  const ns = namespace();
  const jobs: Promise<unknown>[] = [];

  for (let i = 0; i < sessionStorage.length; i += 1) {
    const key = sessionStorage.key(i);
    if (!key) continue;
    const value = sessionStorage.getItem(key);
    if (value === null) continue;

    if (isSessionKey(key)) {
      jobs.push(Preferences.set({ key: PREF_PREFIX + key, value }));
    } else if (isDurableKey(key) && ns) {
      jobs.push(draftStore.setItem<StoredDraft>(key, { ts: Date.now(), value, ns }));
    }
  }

  const results = await Promise.allSettled(jobs);
  const failed = results.filter((r) => r.status === 'rejected').length;
  if (failed > 0) {

    console.warn(`[storage] ${failed} escrituras fallaron al pasar a segundo plano`);
  }
}

/**
 * Engancha el volcado a que la app pase a segundo plano.
 *
 * Se usan DOS señales a propósito. `appStateChange` de Capacitor es la buena y
 * la que llega en el caso normal; `visibilitychange` del WebView es el refuerzo
 * para los caminos en los que el puente ya no responde —que son justo los
 * momentos en los que Android está a punto de matar el proceso—. Volcar dos
 * veces no cuesta nada: la operación es idempotente.
 */
let backgroundFlushInstalled = false;

export function installBackgroundFlush(): void {
  if (!isNative()) return;
  // Idempotente a propósito: lo llaman el arranque normal y la red de seguridad
  // de `main.tsx`, que pueden coincidir. Sin el guard se registrarían los
  // listeners dos veces y cada paso a segundo plano dispararía dos volcados.
  if (backgroundFlushInstalled) return;
  backgroundFlushInstalled = true;

  void CapacitorApp.addListener('appStateChange', ({ isActive }) => {
    if (!isActive) void flushDurableStorage();
  });

  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') void flushDurableStorage();
  });
}

export function installNativeStorageMirror(): void {
  if (!isNative()) return;

  const proto = Object.getPrototypeOf(sessionStorage) as Storage;
  const marker = '__roisDurableMirrorInstalled';
  if ((proto as unknown as Record<string, boolean>)[marker]) return;

  const originalSet = proto.setItem;
  const originalRemove = proto.removeItem;
  const originalClear = proto.clear;

  proto.setItem = function setItem(key: string, value: string): void {
    originalSet.call(this, key, value);
    // `localStorage` comparte prototipo con `sessionStorage`: sin este guard
    // estaríamos replicando también sus escrituras.
    if (this !== sessionStorage) return;

    if (isSessionKey(key)) {
      track(
        Preferences.set({ key: PREF_PREFIX + key, value }).catch((err) => {
           
          console.warn('[storage] no se pudo guardar la sesión', key, err);
        }),
      );
      return;
    }

    if (isDurableKey(key)) {
      const ns = namespace();
      // Sin sesión no hay a quién atribuir el borrador.
      if (!ns) return;
      track(
        draftStore
        .setItem<StoredDraft>(key, { ts: Date.now(), value, ns })
        .catch((err) => {
          // Antes un fallo de cuota se tragaba en SILENCIO y el operario perdía
          // la persistencia sin enterarse. Ahora queda registrado.
           
          console.warn('[storage] no se pudo guardar el borrador', key, err);
        }),
      );
    }
  };

  proto.removeItem = function removeItem(key: string): void {
    originalRemove.call(this, key);
    if (this !== sessionStorage) return;
    if (isSessionKey(key)) {
      void Preferences.remove({ key: PREF_PREFIX + key });
    } else if (isDurableKey(key)) {
      void draftStore.removeItem(key);
    }
  };

  // Salvavidas: si algún camino hace una limpieza nuclear de `sessionStorage`,
  // el nivel duradero tiene que irse con él. Sin esto, la sesión sobreviviría a
  // un cierre de sesión — que no es una molestia, es un fallo de seguridad.
  proto.clear = function clear(): void {
    originalClear.call(this);
    if (this === sessionStorage) void clearNativeStorage();
  };

  (proto as unknown as Record<string, boolean>)[marker] = true;
}

/**
 * Borra la sesión y los borradores del usuario actual. Llamar en TODO camino de
 * logout.
 */
export async function clearNativeStorage(): Promise<void> {
  if (!isNative()) return;
  // El namespace se calcula ANTES de borrar la sesión: después ya no se sabría
  // de quién eran los borradores. También la huella antigua, para no dejar
  // atrás los guardados antes del cambio de dueño (2026-10-12).
  const ns = namespace();
  const legacyNs = legacyNamespace();

  await Promise.all(
    SESSION_KEYS.map((key) =>
      Preferences.remove({ key: PREF_PREFIX + key }).catch(() => undefined)
    )
  );

  if (!ns) return;
  try {
    const keys = await draftStore.keys();
    await Promise.all(
      keys.map(async (key) => {
        const entry = await draftStore.getItem<StoredDraft>(key);
        if (entry && (entry.ns === ns || entry.ns === legacyNs)) {
          await draftStore.removeItem(key);
        }
      })
    );
  } catch {
    // Un borrador que no se deja borrar no puede bloquear el cierre de sesión.
  }
}
